import { FormEvent, useState } from "react";
import { ICreateMinerProfileProps } from "./CreateMinerProfile.props";
import { useDispatch } from "react-redux";
import useApiRequests from "@services/http/useApiRequest";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { makeNotifs } from "@utils/makeNotifs";
import { ALGOS_E } from "@constants/Algos";
import {
  IAddMinerProfileParams,
  AddMinerProfileResponse,
  AddMinerProfile,
} from "@services/minerProfiles";
import { addMinerProfile } from "@store/minerProfiles/actions";
import { IMinerProfile } from "@store/minerProfiles/reducer";
import { calculateHash, HashFactorName } from "@utils/hash.utils";

interface Elems extends HTMLFormControlsCollection {
  name: HTMLInputElement;
  consumpation: HTMLInputElement;
  perfomance: HTMLInputElement;
  efficiency: HTMLInputElement;
  width: HTMLInputElement;
  height: HTMLInputElement;
  depth: HTMLInputElement;
  hash: HTMLInputElement;
}

interface MyForm extends HTMLFormElement {
  readonly elements: Elems;
}

export const useCreateMinerProfileControl = (
  props: ICreateMinerProfileProps
) => {
  const dispatch = useDispatch();
  const { onSuccess } = useGlobalNotifs();
  const { ok } = makeNotifs();
  const { enqueue } = useApiRequests();
  const [errors, setErrors] = useState<{ [key: string]: boolean }>();
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (e: FormEvent<MyForm>) => {
    e.preventDefault();
    setErrors(undefined);

    let err = false;

    const elements = e.currentTarget.elements;
    let params: IAddMinerProfileParams = {
      name: "",
      hash: ALGOS_E.SHA256,
      consumption: 0,
      performance: 0,
      meta: "",
    };

    let values: {
      name: string;
      consumpation: string;
      perfomance: string;
      efficiency: string;
      width: string;
      height: string;
      depth: string;
      hash: ALGOS_E;
      err?: string;
    } = {
      name: "",
      consumpation: "",
      perfomance: "",
      efficiency: "",
      width: "",
      height: "",
      depth: "",
      hash: ALGOS_E.SHA256,
    };

    const requiredFields: (keyof Elems)[] = [
      "name",
      "consumpation",
      "perfomance",
      "efficiency",
      "width",
      "height",
      "depth",
      "hash",
    ];

    for (const fieldName of requiredFields) {
      const elem = elements[fieldName];
      if (!elem || !(elem instanceof HTMLInputElement)) {
        err = true;
        setErrors((prev) => {
          if (!prev) {
            return {
              [fieldName]: true,
            };
          } else {
            return { ...prev, [fieldName]: true };
          }
        });
        continue;
      }

      const dataBage = elem.dataset?.bage as HashFactorName;
      const value =
        fieldName === "perfomance"
          ? calculateHash(elem.value, dataBage)
          : elem.value;

      if (!value) {
        err = true;
        setErrors((prev) => {
          if (!prev) {
            return {
              [fieldName]: true,
            };
          } else {
            return { ...prev, [fieldName]: true };
          }
        });
      }

      values = {
        ...values,
        [fieldName]: value,
      };
    }

    if (!!err) {
      return;
    }

    params = {
      ...params,
      name: values.name,
      consumption: Number(values.consumpation),
      performance: Number(values.perfomance),
      hash: values.hash,
      meta: JSON.stringify({
        width: values.width,
        height: values.height,
        depth: values.depth,
        efficiency: values.efficiency,
        err: values.err,
      }),
    };

    const res = await enqueue<AddMinerProfileResponse>(
      new AddMinerProfile({ ...params }),
      {
        onLoadingStatusChanged: setLoading,
      }
    );
    if (!res) return;

    props.onCreate?.(res.Record);
    dispatch(addMinerProfile({ ...params, id: res.Record } as IMinerProfile));
    onSuccess(ok.success);
    props.handleDestroy();
  };

  return {
    loading,
    errors,

    onSubmit,
  };
};
